import { useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform, useMotionValueEvent } from "framer-motion";
import { EASE } from "./Motion";

export default function Pipeline({ steps, lineTestid }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.5"] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  const lineScale = useTransform(progress, [0, 1], [0, 1]);
  const [activeCount, setActiveCount] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) =>
    setActiveCount(Math.round(v * steps.length))
  );

  const nodeStyle = (active) =>
    active
      ? {
          scale: 1,
          backgroundColor: "rgb(var(--gold))",
          color: "rgb(var(--ink))",
          borderColor: "rgb(var(--gold) / 0.9)",
          boxShadow: "0 0 28px rgb(var(--gold) / 0.45)",
        }
      : {
          scale: 0.92,
          backgroundColor: "rgba(18,19,26,0.85)",
          color: "#6b6b74",
          borderColor: "rgba(255,255,255,0.12)",
          boxShadow: "0 0 0 rgba(0,0,0,0)",
        };

  return (
    <div ref={ref}>
      <div className="relative hidden md:block">
        <div className="absolute left-0 right-0 top-7 h-px bg-white/10" />
        <motion.div
          style={{ scaleX: lineScale }}
          className="absolute left-0 right-0 top-7 h-px origin-left bg-gradient-to-r from-goldlight via-gold to-goldbright shadow-[0_0_12px_rgb(var(--gold)_/_0.6)]"
          data-testid={lineTestid}
        />
        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0,1fr))` }}
        >
          {steps.map((s, i) => {
            const active = i < activeCount;
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex flex-col items-center px-3 text-center" data-testid={s.testid}>
                <motion.div
                  initial={false}
                  animate={nodeStyle(active)}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border"
                >
                  <Icon size={20} strokeWidth={1.75} />
                </motion.div>
                <div className="mt-6 font-mono text-[10px] tracking-[0.25em] text-gold/70">
                  STEP 0{i + 1}
                </div>
                <h4 className={`mt-2 text-lg font-semibold transition-colors duration-500 ${active ? "text-neutral-50" : "text-neutral-500"}`}>
                  {s.title}
                </h4>
                <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-neutral-400">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative md:hidden">
        <div className="absolute bottom-4 left-[23px] top-4 w-px bg-white/10" />
        <motion.div
          style={{ scaleY: lineScale }}
          className="absolute bottom-4 left-[23px] top-4 w-px origin-top bg-gradient-to-b from-goldlight via-gold to-goldbright"
        />
        <div className="space-y-10">
          {steps.map((s, i) => {
            const active = i < activeCount;
            const Icon = s.icon;
            return (
              <div key={s.title} className="relative flex gap-6" data-testid={`${s.testid}-mobile`}>
                <motion.div
                  initial={false}
                  animate={nodeStyle(active)}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border"
                >
                  <Icon size={18} strokeWidth={1.75} />
                </motion.div>
                <div className="pt-1">
                  <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">STEP 0{i + 1}</div>
                  <h4 className={`mt-1 text-lg font-semibold transition-colors duration-500 ${active ? "text-neutral-50" : "text-neutral-500"}`}>
                    {s.title}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-400">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
